import { DesktopIcon, MoonIcon, SunIcon } from '@phosphor-icons/react';
import { useCallback } from 'react';

import { type ThemeMode, useTheme } from '@/contexts/ThemeContext';
import { userService } from '@/services/data/UserService';
import { logger } from '@/utils/logger';
import { showErrorToast } from '@/utils/toast';

/** Canonical appearance options — shared so Settings → Profile and the
 * sidebar's user menu render the identical three-way toggle. */
export const APPEARANCE_OPTIONS = [
  { value: 'system', label: 'System', icon: DesktopIcon },
  { value: 'light', label: 'Light', icon: SunIcon },
  { value: 'dark', label: 'Dark', icon: MoonIcon },
] as const;

/**
 * `ThemeContext.setThemeMode` only updates local/session state (matches its
 * own README: contexts own UI state, not server state). Every caller that
 * lets a user CHOOSE a theme — not just read the current one — needs the
 * write-through to `user_metadata.theme_mode` too, or the choice silently
 * doesn't survive a login on another device. One hook, so a second toggle
 * (the sidebar) can't drift from Settings' persistence behavior.
 */
export function useAppearanceToggle() {
  const { themeMode, setThemeMode } = useTheme();

  const setAppearance = useCallback(
    async (nextMode: ThemeMode): Promise<void> => {
      setThemeMode(nextMode);
      const resolvedDark =
        nextMode === 'system'
          ? window.matchMedia('(prefers-color-scheme: dark)').matches
          : nextMode === 'dark';
      const { error } = await userService.updateUserProfile({
        theme_mode: nextMode,
        // Keep the old boolean populated for older clients while
        // `theme_mode` becomes the preference source of truth.
        dark_mode: resolvedDark,
      });
      if (error) {
        logger.error('Error saving appearance:', error);
        showErrorToast('Update failed', error.message);
      }
    },
    [setThemeMode],
  );

  return { themeMode, setAppearance, options: APPEARANCE_OPTIONS };
}
